/*  Shared  */
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { TenantLicenseUsage } from 'application/Shared/types/tenantLicenseUsage';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useEffect, useState } from 'react';

interface UseFetchTenantLicenseUsageParams {
    applicationId: string;
    clientId: string;
}

interface UseFetchTenantLicenseUsageResponse {
    loading: boolean;
    refetchData: () => void;
    refetching: boolean;
    tenantLicenseUsage: TenantLicenseUsage[];
}

interface TenantLicenseUsageResponseData {
    data: TenantLicenseUsage[];
    message: string;
    serverResponse: number;
}

export const useFetchTenantLicenseUsage = ({
    applicationId,
    clientId
}: UseFetchTenantLicenseUsageParams): UseFetchTenantLicenseUsageResponse => {
    const [tenantLicenseUsage, setTenantLicenseUsage] = useState<
        TenantLicenseUsage[]
    >([]);

    const [loading, setLoading] = useState(false);
    const [refetching, setRefetching] = useState(false);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => {
        setShouldRefetch({});
        setRefetching(true);
    };

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const params = {
            applicationId,
            includeAccounts: true,
            includeApplication: true,
            includeApplicationUsage: true,
            includeClients: true,
            platformTenantId: clientId,
            size: -1
        };

        const fetchData = async () => {
            setLoading(true);
            if (!clientId || clientId === '0') {
                setTenantLicenseUsage([]);
                setLoading(false);
                return;
            }
            try {
                const response =
                    await axios.get<TenantLicenseUsageResponseData>(
                        `/tenantLicenseUsage`,
                        {
                            cancelToken: source.token,
                            params
                        }
                    );
                if (!Response.hasData(response)) {
                    setTenantLicenseUsage([]);
                    setLoading(false);
                    return;
                }

                const data = Response.getData(response);
                setTenantLicenseUsage(data);
                setLoading(false);
            } catch (err) {
                // Only reset states if it's not a cancellation error
                if (!axios.isCancel(err)) {
                    debug.e('Unable to fetch Tenant License Usage.', err);
                    setTenantLicenseUsage([]);
                    setLoading(false);
                }
            }
        };
        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [applicationId, clientId, shouldRefetchData]);

    return {
        loading,
        refetchData,
        refetching,
        tenantLicenseUsage
    };
};
